import { ClassType, EntityType } from '@frontend/common';
import { StatusBadge, accountIdFromResolvedEntity } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { NotificationHtmlPreview } from '@frontend/notification/components';
import { NotificationExtended, NotificationStatus } from '@frontend/notification/types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';

export const NotificationDetailsCard = ({ notification }: { notification: NotificationExtended }) => (
    <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
        <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
        <PlatformDetailGrid
            object={notification}
            cols={2}
            order={
                notification.status === NotificationStatus.PENDING
                    ? ['channel', 'language', 'user', 'transaction', 'status', 'creation_timestamp', 'delayed_timestamp']
                    : ['channel', 'language', 'user', 'transaction', 'status', 'creation_timestamp']
            }
            keyOverwrite={new Map([['transaction', CommonMessage.OBJECTS.TRANSACTION.DEFAULT]])}
            valueOverwrite={
                new Map<string, (obj: NotificationExtended) => React.ReactNode>([
                    [
                        'user',
                        (obj) =>
                            obj.user ? (
                                <PlatformObjectBadge
                                    entityType={EntityType.USER}
                                    id={obj.user.entity_id}
                                    display={obj.user.name || ''}
                                />
                            ) : (
                                <span>-</span>
                            )
                    ],
                    [
                        'transaction',
                        (obj) =>
                            obj.transaction ? (
                                <PlatformObjectBadge
                                    entityType={EntityType.TRANSACTION}
                                    id={obj.transaction.entity_id}
                                    display={obj.transaction.name}
                                    foreign={obj.transaction.foreign}
                                    accountId={accountIdFromResolvedEntity(obj.transaction)}
                                />
                            ) : (
                                <span>-</span>
                            )
                    ],
                    [
                        'status',
                        (obj) => (
                            <StatusBadge
                                value={obj.status}
                                colorMap={
                                    new Map([
                                        [[NotificationStatus.FAILED], ClassType.DANGER],
                                        [[NotificationStatus.PENDING], ClassType.INFO],
                                        [[NotificationStatus.SKIPPED], ClassType.DISABLED],
                                        [[NotificationStatus.SUCCEEDED], ClassType.SUCCESS]
                                    ])
                                }
                            />
                        )
                    ]
                ])
            }
        />
    </div>
);

export const NotificationMessageSection = ({ notification }: { notification: NotificationExtended }) => (
    <div className='bg-background-surface border-border-default flex flex-col gap-4 rounded-xl border p-4'>
        <h4 className='text-foreground-default text-lg font-semibold'>
            <FormattedMessage
                id='notification.detail.message'
                description='Heading for the message card on the notification detail page'
                defaultMessage='Message'
            />
        </h4>
        <div className='flex flex-col gap-1'>
            <span className='text-foreground-muted text-sm'>
                <FormattedMessage
                    id='notification.detail.subject'
                    description='Label for the subject field on the notification detail page'
                    defaultMessage='Subject'
                />
            </span>
            <span className='text-foreground-default text-base font-medium'>{notification.subject || '-'}</span>
        </div>
        <div className='flex flex-col gap-1'>
            <span className='text-foreground-muted text-sm'>
                <FormattedMessage
                    id='notification.detail.body'
                    description='Label for the body field on the notification detail page'
                    defaultMessage='Body'
                />
            </span>
            <NotificationHtmlPreview
                body={notification.body}
                blobs={notification.blobs}
                channel={notification.channel}
            />
        </div>
    </div>
);
